import React, { useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CSSTransition } from "react-transition-group";

import styles from "./phoneme.module.css";
import * as phonemeActions from "../../redux/actions/phoneme";
import { State } from "../../redux/store";
import { srcPath } from "../../util/Path";
import { displayLetter } from "../../util/Util";

const balloonStyle = (index: number): any => {
  const type = ((index + 1) % 4) + 1;
  let top: number, left: number;
  switch (index) {
    case 0:
      top = 250;
      left = -570;
      break;
    case 1:
      top = 80;
      left = -470;
      break;
    case 2:
      top = 270;
      left = -340;
      break;
    case 3:
      top = 60;
      left = -250;
      break;
    case 4:
      top = 160;
      left = -100;
      break;
    case 5:
      top = 20;
      left = 30;
      break;
    case 6:
      top = 200;
      left = 140;
      break;
    case 7:
      top = 30;
      left = 260;
      break;
    case 8:
      top = 300;
      left = 350;
      break;
    case 9:
      top = 100;
      left = 440;
      break;
  }
  return {
    top: `${top - 20}px`,
    left: `calc(50% + ${left}px)`,
  };
};

interface Props {
  itemLeft: number;
}

interface BalloonProps {
  phonics: string;
  index: number;
  balloonSelected: boolean;
  balloonCorrect: boolean;
}

const Balloon = (props: BalloonProps) => {
  const { phonics, index, balloonSelected, balloonCorrect } = props;

  const dispatch = useDispatch();
  const {
    balloonColors,
    balloonReady,
    balloonCanSelect,
    balloonAnswering,
  } = useSelector((state: State) => state.phoneme);

  const balloonSrc = srcPath(
    `phoneme/balloons/balloon_${balloonColors[index]}.png`
  );

  const balloonSelectedSrc = srcPath(
    balloonCorrect ? "phoneme/balloons/star.png" : "shared/answer_wrong.png"
  );

  const clickBalloon = async () => {
    if (!balloonSelected && balloonCanSelect) {
      await dispatch(phonemeActions.selectBalloon(index));
      dispatch(phonemeActions.checkBalloonEnd());
    }
  };

  let shownPhonics = displayLetter(phonics);

  return (
    <div
      className={styles.balloonContainer}
      style={
        balloonReady
          ? balloonStyle(index)
          : { top: 400, left: "50%", transform: "translateX(-50%)" }
      }
      data-delay={balloonSelected ? 0 : (index % 4) + 1}
    >
      {balloonSelected && (
        <CSSTransition
          in={true}
          appear
          timeout={0}
          classNames={{
            appear: styles.balloonSelectedAppear,
            appearDone: styles.balloonSelectedAppearDone,
          }}
        >
          <img
            className={styles.balloonSelected}
            style={{
              transform: `scale(${balloonCorrect ? 1.3 : 1})`,
            }}
            src={balloonSelectedSrc}
            alt=""
          />
        </CSSTransition>
      )}
      {!balloonSelected && <img className="balloon" src={balloonSrc} alt="" />}
      <div
        className={styles.balloonClick}
        style={{ cursor: balloonSelected ? "context-menu" : "pointer" }}
        onClick={() => clickBalloon()}
      >
        <div
          className={styles.balloonText}
          style={{
            top: `${
              (balloonSelected && !balloonCorrect ? 12 : 22) +
              (shownPhonics.length - 1) * 2
            }px`,
            fontSize: `${70 - (shownPhonics.length - 1) * 5}px`,
          }}
        >
          {shownPhonics}
        </div>
      </div>
    </div>
  );
};

const BalloonPage = (props: Props) => {
  const pageRef = useRef<HTMLDivElement>();

  const {
    mainPhonics,
    balloons,
    balloonAnswered,
    balloonPhonics,
    balloonReadyExit,
  } = useSelector((state: State) => state.phoneme);

  const balloonSelected = (index: number): boolean => {
    return balloonAnswered.includes(index);
  };

  const balloonCorrect = (balloon: string): boolean => {
    return balloonPhonics.includes(balloon);
  };

  useEffect(() => {
    pageRef.current.classList.toggle(styles.balloonPageOn);
  }, []);

  useEffect(() => {
    if (balloonReadyExit) {
      pageRef.current.classList.toggle(styles.balloonPageOn);
    }
  }, [balloonReadyExit]);

  return (
    <div ref={pageRef} className={styles.balloonPage}>
      {balloons.map((balloon, index) => (
        <Balloon
          key={index}
          phonics={balloon}
          index={index}
          balloonSelected={balloonSelected(index)}
          balloonCorrect={balloonCorrect(balloon)}
        />
      ))}
      <img
        className={styles.balloonGameIcon}
        alt=""
        src={srcPath(`phoneme/game_icons/icon_${mainPhonics}.png`)}
      />
    </div>
  );
};

export default BalloonPage;
